import { useEffect, KeyboardEvent } from "react";
import { observer } from "mobx-react";
import { Modal, Form, Input } from "antd";
import { useTranslation } from "react-i18next";
import { validatePassword } from "logic/src/utils";
import SJButton from "@/components/SJButton";
import PasswordEye from "@/components/PasswordEye";
import useMessage from "logic/src/utils/useMessage";
import { ReactComponent as CloseIcon } from "logic/src/components/icons/close.svg";
import { AUTH_ERROR_TIP, LOGIN_TYPE } from "logic/src/constants/user";
import authVM from "logic/src/businesses/auth/vm";
import styles from "./index.module.scss";

const Password: React.FC<{
  visible: boolean;
  onClose: () => void;
  onFinish: () => void;
}> = ({ visible, onClose, onFinish }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const message = useMessage();

  const origin = Form.useWatch("origin", form);
  const password = Form.useWatch("password", form);
  const confirm = Form.useWatch("confirm", form);

  const btnClick = authVM.user.userInfo?.need_init_passwd
    ? password && confirm
    : origin && password && confirm;

  const showToast = (text: string) => {
    message.warning(text);
  };

  useEffect(() => {
    if (visible) {
      form.resetFields();
    }
  }, [visible]);

  const handleInitassword = async () => {
    try {
      const checkSuccess = await form
        .validateFields(["password", "confirm"])
        .then(() => true)
        .catch(() => false);
      if (!checkSuccess) return;
      await authVM.initPassword({
        password,
        confirm,
      });
      message.success({
        type: "success",
        content: "密码设置成功",
      });
      onFinish();
    } catch (error: any) {
      if (error && error.code) {
        showToast(AUTH_ERROR_TIP[error.code] || "请求失败");
      } else {
        throw error;
      }
    }
  };

  const handleEditPassword = async () => {
    try {
      const checkSuccess = await form
        .validateFields(["origin", "password", "confirm"])
        .then(() => true)
        .catch(() => false);
      if (!checkSuccess) return;
      await authVM.editPassword({
        origin,
        password,
      });
      showToast("密码修改成功");
      onFinish();
    } catch (error: any) {
      if (error && error.code) {
        showToast(AUTH_ERROR_TIP[error.code] || "请求失败");
      } else {
        throw error;
      }
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter") {
      authVM.user.userInfo?.need_init_passwd
        ? handleInitassword()
        : handleEditPassword();
    }
  };

  return (
    <>
      <Modal
        className={styles.container}
        title={
          authVM.user.userInfo?.need_init_passwd
            ? t("sensechat-284")
            : t("sensechat-285")
        } // "设置密码" : "修改密码"
        width="464px"
        centered
        open={visible}
        onCancel={onClose}
        closeIcon={<CloseIcon style={{ fontSize: 24 }} />}
        footer={[
          <SJButton
            block
            size="large"
            key="submit"
            type="primary"
            disabled={!btnClick}
            onClick={
              authVM.user.userInfo?.need_init_passwd
                ? handleInitassword
                : handleEditPassword
            }
            className={styles["confirm-button"]}
          >
            {t("sensechat-57")}
            {/* 确定 */}
          </SJButton>,
        ]}
      >
        <Form form={form} layout="horizontal" className={styles.form}>
          {!authVM.user.userInfo?.need_init_passwd && (
            <Form.Item
              className={styles["form-item"]}
              name="origin"
              validateFirst
              validateTrigger="onConfirm"
              rules={[
                {
                  required: true,
                  min: 6,
                  message: t("sensechat-277") as string, // "密码至少6位"
                },
                {
                  validator: (_: any, value: string) => {
                    if (!validatePassword(value)) {
                      return Promise.reject(
                        new Error(t("sensechat-278") as string),
                      ); // 密码必须同时包含数字及大小写字母
                    } else {
                      return Promise.resolve();
                    }
                  },
                },
              ]}
            >
              <Input.Password
                placeholder={t("sensechat-288") as string} // "请输入旧密码"
                autoComplete="off"
                maxLength={100}
                className="large-input dailog-input"
                iconRender={PasswordEye}
                onKeyDown={handleKeyDown}
              />
            </Form.Item>
          )}

          <Form.Item
            className={styles["form-item"]}
            name="password"
            validateFirst
            validateTrigger="onConfirm"
            rules={[
              {
                required: true,
                min: 6,
                message: t("sensechat-277") as string, // "密码至少6位"
              },
              {
                validator: (_: any, value: string) => {
                  if (!validatePassword(value)) {
                    return Promise.reject(
                      new Error(t("sensechat-278") as string),
                    ); // 密码必须同时包含数字及大小写字母
                  } else {
                    return Promise.resolve();
                  }
                },
              },
            ]}
          >
            <Input.Password
              placeholder={t("sensechat-289") as string} // 请输入新密码
              autoComplete="off"
              maxLength={100}
              className="large-input dailog-input"
              iconRender={PasswordEye}
              onKeyDown={handleKeyDown}
            />
          </Form.Item>

          <Form.Item
            className={styles["form-item"]}
            name="confirm"
            validateFirst
            validateTrigger="onConfirm"
            rules={[
              {
                required: true,
                min: 6,
                message: t("sensechat-277") as string, // "密码至少6位"
              },
              {
                validator: (_: any, value: string) => {
                  if (!validatePassword(value)) {
                    return Promise.reject(
                      new Error(t("sensechat-278") as string),
                    ); // 密码必须同时包含数字及大小写字母
                  } else {
                    return Promise.resolve();
                  }
                },
              },
              {
                validator(_, value) {
                  if (password !== value) {
                    return Promise.reject(
                      new Error(t("sensechat-281") as string),
                    ); // 新密码与确认新密码不同
                  } else {
                    return Promise.resolve();
                  }
                },
              },
            ]}
          >
            <Input.Password
              placeholder={t("sensechat-290") as string} // "请再次输入新密码"
              autoComplete="off"
              maxLength={100}
              className="large-input dailog-input"
              iconRender={PasswordEye}
              onKeyDown={handleKeyDown}
            />
          </Form.Item>
        </Form>
      </Modal>
      {message.contextHolder}
    </>
  );
};

export default observer(Password);
